import React, { useState, useEffect } from 'react';
import SessionsTable from './components/SessionsTable';
import VconsTable from './components/VconsTable';
import AskPage from './components/AskPage';
import AnalyticsPage from './components/AnalyticsPage';
import TerritoriesPage from './components/TerritoriesPage';
import ConsentPage from './components/ConsentPage';
import PeoplePage from './components/PeoplePage';
import ThreadsPage from './components/ThreadsPage';
import ConsentPortal from './components/ConsentPortal';
import LoginPage from './components/LoginPage';
import SettingsPage from './components/settings/SettingsPage';
import LogsPage from './components/logs/LogsPage';
import StatusPage from './components/status/StatusPage';
import MetricsHistory from './components/status/MetricsHistory';
import AboutPage from './components/AboutPage';
import { authService, AuthUser } from './services/api';
import './App.css';

function App() {
  const [activeTab, setActiveTab] = useState<'sessions' | 'vcons' | 'threads' | 'ask' | 'analytics' | 'consent' | 'people' | 'territories' | 'settings' | 'logs' | 'status' | 'history' | 'about'>('status');
  // Set when a citation in the Ask tab is clicked, so the VCons tab opens
  // filtered to that conversation. Lives here because the two tabs are
  // siblings and neither can reach the other directly.
  const [vconFocus, setVconFocus] = useState<string | undefined>(undefined);

  const openVcon = (uuid: string) => {
    setVconFocus(uuid);
    setActiveTab('vcons');
  };
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  // The data-subject consent portal renders BEFORE any auth: a party
  // arriving via their tokenized invite link must never see the console
  // login. The token is the sole credential (validated server-side).
  const consentToken = new URLSearchParams(window.location.search).get('consent_token');

  useEffect(() => {
    if (consentToken) return; // portal visitors never hit the console API
    authService.getMe()
      .then(setUser)
      .finally(() => setAuthChecked(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (consentToken) {
    return <ConsentPortal token={consentToken} />;
  }

  const handleLogout = async () => {
    await authService.logout();
    setUser(null);
  };

  if (!authChecked) {
    return <div className="loading">Loading…</div>;
  }

  if (!user) {
    return <LoginPage onLogin={setUser} />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <div>
          <div className="header-eyebrow">12by9 · Governance Control Plane</div>
          <h1>Consent &amp; Governance Console</h1>
        </div>
        <nav className="App-nav">
          <button
            className={`nav-button ${activeTab === 'status' ? 'active' : ''}`}
            onClick={() => setActiveTab('status')}
          >
            Status
          </button>
          <button
            className={`nav-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
          <button
            className={`nav-button ${activeTab === 'sessions' ? 'active' : ''}`}
            onClick={() => setActiveTab('sessions')}
          >
            Recording Sessions
          </button>
          <button
            className={`nav-button ${activeTab === 'vcons' ? 'active' : ''}`}
            onClick={() => setActiveTab('vcons')}
          >
            VCons
          </button>
          <button
            className={`nav-button ${activeTab === 'threads' ? 'active' : ''}`}
            onClick={() => setActiveTab('threads')}
          >
            Threads
          </button>
          <button
            className={`nav-button ${activeTab === 'ask' ? 'active' : ''}`}
            onClick={() => setActiveTab('ask')}
          >
            Ask
          </button>
          <button
            className={`nav-button ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            Analytics
          </button>
          <button
            className={`nav-button ${activeTab === 'consent' ? 'active' : ''}`}
            onClick={() => setActiveTab('consent')}
          >
            Consent
          </button>
          <button
            className={`nav-button ${activeTab === 'people' ? 'active' : ''}`}
            onClick={() => setActiveTab('people')}
          >
            People
          </button>
          <button
            className={`nav-button ${activeTab === 'territories' ? 'active' : ''}`}
            onClick={() => setActiveTab('territories')}
          >
            Territories
          </button>
          {/* Participants tab hidden for now — the page needs rethinking to
              be genuinely useful (see ParticipantsTable.tsx, still in the tree). */}
          <button
            className={`nav-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
          <button
            className={`nav-button ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            Logs
          </button>
          <button
            className={`nav-button ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            About
          </button>
        </nav>
        <div className="user-bar">
          <span>{user.name}</span>
          <button className="logout-button" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      <main className="App-main">
        {activeTab === 'sessions' && <SessionsTable />}
        {activeTab === 'vcons' && <VconsTable initialUuid={vconFocus} />}
        {activeTab === 'threads' && <ThreadsPage />}
        {activeTab === 'ask' && <AskPage onOpenVcon={openVcon} />}
        {activeTab === 'analytics' && <AnalyticsPage />}
        {activeTab === 'consent' && <ConsentPage />}
        {activeTab === 'people' && <PeoplePage />}
        {activeTab === 'territories' && <TerritoriesPage />}
        {activeTab === 'settings' && <SettingsPage />}
        {activeTab === 'logs' && <LogsPage />}
        {activeTab === 'status' && <StatusPage />}
        {activeTab === 'history' && <MetricsHistory />}
        {activeTab === 'about' && <AboutPage />}
      </main>
    </div>
  );
}

export default App;
